import PropTypes from 'prop-types';
import React, { useState, useEffect, forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { useForm } from 'react-hook-form';
import { Col, Row, Form } from 'react-bootstrap';

import { theme } from '_constants/theme';
import { FIELDS_KEYS } from '_constants/forms';
import { getFieldProps } from '_helpers/forms/fields';
import { hexToRGBA } from '_helpers/theme';

import BaseField from '_components/atomic/fields/BaseField';
import Button from '_components/atomic/Button';

const StyledContainer = styled(Form)`
  max-width: 570px;
  width: 100%;
  padding-top: ${theme.spaces.large + 2}px;

  button {
    width: 100%;
    margin-bottom: ${theme.spaces.regular}px;
    justify-content: center;
    box-shadow: 0px 1px 2px 0px #1018280d;
  }

  a {
    color: ${theme.colors.white};
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledMessageContainer = styled(forwardRef(({ error, ...props }, ref) => <div ref={ref} {...props} />))`
  display: inline-flex;
  margin-bottom: ${theme.spaces.regular}px;
  font-family: 'Inter';
  font-size: ${theme.fontSizes.regular}px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: -0.03em;
  color: ${theme.colors.white};

  ${({ error }) =>
    error &&
    css`
      color: ${theme.colors.red.primary};
    `}
`;

const TalkToExpertForm = ({ error, success, submitDisabled, onSubmit }) => {
  const {
    control,
    formState: { isValidating },
    reset,
    handleSubmit,
  } = useForm({
    reValidateMode: 'onBlur',
  });

  const [emailVerifying, setEmailVerifying] = useState(false);

  useEffect(() => {
    if (success) {
      reset();
    }
  }, [success, reset]);

  return (
    <StyledContainer noValidate onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col xs={12} md={8}>
          <BaseField
            {...getFieldProps(FIELDS_KEYS.email, {
              validationOptions: { setProcessing: setEmailVerifying },
            })}
            label={undefined}
            placeholder="Email*"
            textColor={theme.colors.white}
            placeholderColor={hexToRGBA(theme.colors.white, 0.8)}
            borderColor={hexToRGBA(theme.colors.indigo.light, 0.3)}
            backgroundColor={hexToRGBA(theme.colors.white, 0.1)}
            control={control}
            loading={emailVerifying}
            feedback={emailVerifying ? 'Verifying...' : undefined}
          />
        </Col>
        <Col xs={12} md={4}>
          <Button
            type="submit"
            secondary
            disabled={emailVerifying || isValidating || submitDisabled}
          >
            Talk to an Expert
          </Button>
        </Col>
      </Row>
      {success && (
        <StyledMessageContainer>
          Thank you for your inquiry. Our agents will respond as quickly as
          possible.
        </StyledMessageContainer>
      )}
      {error && (
        <StyledMessageContainer error>{error?.message}</StyledMessageContainer>
      )}
    </StyledContainer>
  );
};

TalkToExpertForm.propTypes = {
  error: PropTypes.object,
  submitDisabled: PropTypes.bool,
  onSubmit: PropTypes.func,
};

export default TalkToExpertForm;
